'use client';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Image from 'next/image';

import { ExperienceMedia } from '@/services/experienceTypes';
import { Village, VillageMedia } from '@/services/types';
import { yupResolver } from '@hookform/resolvers/yup';

import { ICountriesDictionary } from '@/dictionaries/utils/countries/types';
import { IEnquiryFormDictionary } from '@/dictionaries/components/enquiryForm/types';
import { IModalsDictionary } from '@/dictionaries/components/modals/types';

import { getTranslatedSchema } from './villageSchema';
import { Locale } from '../../../i18n.config';
import { IDatePickerDictonary } from '@/dictionaries/components/datePicker/types';
import SubmittedModal from './SubmittedModal';

import GlobalLoader from '../GlobalLoader';
import getMediaFilterUrl from '@/utils/get-media-filter';
import VillageEnquiryForm from './VillageEnquiryForm';

type EnquiryFormProps = {
  village: Village;
  lang: Locale;
  media: ExperienceMedia[] | VillageMedia[];
  description: string;
  bannerContent?: JSX.Element;
  formDict: IEnquiryFormDictionary;
  countriesDict: ICountriesDictionary;
  datePickerDict: IDatePickerDictonary;
  modalsDict: IModalsDictionary;
};

export default function VillageEnquiry({
  village,
  lang,
  media,
  description,
  bannerContent,
  formDict,
  countriesDict,
  datePickerDict,
  modalsDict,
}: EnquiryFormProps) {
  const [successModalIsOpen, setSuccessModalIsOpen] = useState(false);

  const schema = getTranslatedSchema(formDict.errors);

  const form = useForm({
    resolver: yupResolver(schema),
    defaultValues: {},
  });

  const coverMedia = media?.filter((medias) => medias.type == 'cover')[0];

  const filteredMediaCover = getMediaFilterUrl({
    mediaFilters: coverMedia.mediaFilters,
    filterName: '1920w_webp',
  });

  const imgCover = `${coverMedia?.path}${coverMedia?.filename}`;

  useEffect(() => {
    if (successModalIsOpen) {
      var root = document.getElementsByTagName('html')[0];
      document.body.classList.add('no-scroll');
      root.classList.add('no-scroll');
    } else {
      var root = document.getElementsByTagName('html')[0];
      document.body.classList.remove('no-scroll');
      root.classList.remove('no-scroll');
    }
  }, [successModalIsOpen]);

  return (
    <>
      <GlobalLoader isLoading={form.formState.isSubmitting} />
      {successModalIsOpen && (
        <SubmittedModal
          formDict={formDict}
          setOpenSubmittedModal={setSuccessModalIsOpen}
        />
      )}
      <div
        id="enquiry-form"
        className="flex flex-col desktop:flex-row w-full h-auto mb-8 scroll-m-20">
        <div className="flex desktop:w-1/3">
          <div className="desktop:shadow-default bg-white z-[1] w-full desktop:mt-[3.4375rem] desktop:mb-0 desktop:h-fit">
            <div className="desktop:px-10 desktop:py-8 py-8 px-[40px] mb-0">
              <h3 className="text-4xl font-averta-semibold mb-2">
                {formDict.makeAnEnquiry}
              </h3>
              <p className="leading-[1.6] mb-4">{description}</p>
            </div>
            <div className="relative h-44">
              <Image
                src={
                  filteredMediaCover ??
                  `${process.env.NEXT_PUBLIC_S3_URI}/thumbs/1920w_webp/${imgCover}`
                }
                alt="Enquiry"
                fill
                className="opacity-100 transition-all duration-300 ease-in-out w-full h-full object-cover object-[50%_50%] inline-block align-middle max-w-full"
                sizes="(min-width: 1280px) 80vw, (min-width: 767px) 90vw, (min-width: 480px) 100vw, 100vw"
                style={{
                  objectFit: 'cover',
                }}
              />
              <div className="absolute flex flex-col items-start justify-center w-full h-full px-10 py-5 top-0 left-0 bg-black/[.4]">
                {bannerContent}
              </div>
            </div>
          </div>
        </div>

        <div className="flex w-full desktop:w-2/3">
          <VillageEnquiryForm
            village={village}
            form={form}
            lang={lang}
            formDict={formDict}
            countriesDict={countriesDict}
            modalsDict={modalsDict}
            datePickerDict={datePickerDict}
            setSuccessModalIsOpen={setSuccessModalIsOpen}
          />
        </div>
      </div>
    </>
  );
}
